import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

import CustomSelect from 'components/CustomSelect';
import Pagination from 'components/Pagination';
import FilterSearch from 'components/FilterSearch';
import AcceptanceMaterials from './modals/AcceptanceMaterials';
import RecipeModal from './modals/RecipeModal';
import UpdateProgress from './modals/UpdateProgress';
import ProduceOrder from './modals/ProduceOrder';
import WarehouseReplenishment from 'pages/production/modals/RequestRaw';

import { GET, httpOk, POST } from 'helpers/api';
import { DOWNLOAD_FILE, formatDate, formatMoney } from 'helpers/helpers';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as OrdersIcon } from 'assets/icons/sidebar/orders.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { useSelector } from 'react-redux';

function Production() {
    const { t } = useTranslation()
    const statuses = [
        {
            id: 'planned',
            name: t('planned')
        },
        {
            id: 'in_progress',
            name: t('in_work')
        },
        {
            id: 'completed',
            name: t('completed')
        },
        {
            id: 'cancelled',
            name: t('cancelled')
        },
    ];

    const reduxAccount = useSelector(state => state.account)

    const [acceptanceMaterialsModal, setAcceptanceMaterialsModal] = useState(false)
    const [recipeModal, setRecipeModal] = useState({ show: false, id: '' })
    const [updateProgressModal, setUpdateProgressModal] = useState({ show: false, item: {} })
    const [produceModal, setProduceModal] = useState({ show: false, item: {} })
    const [warehouseReplenishmentModal, setWarehouseReplenishmentModal] = useState({ show: false })

    const initFilterData = {
        page: 1,
        page_size: 20,
        search: '',
        status: '',
    }

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({})

    async function inProgressOrder(item) {
        const response = await POST(`/production/orders/${item.id}/start/`)
        if (httpOk(response)) {
            getData()
        }
    }

    async function exportFile() {
        await DOWNLOAD_FILE('/production/orders/export/', filterData, t('production'))
    }

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const [response, responseStats] = await Promise.all([
            GET('/production/dashboard/', filterData),
            GET('/production/orders/', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, results: responseStats.data, });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <div className="d-between">
                <div>
                    <div className="main-content-title">
                        {t('production')}
                    </div>

                    <div className="main-content-subtitle">
                        {t('production_orders_management')}
                    </div>
                </div>

                {reduxAccount.role === "TECHNOLOGIST" &&
                    <div className="d-flex gap-8">
                        <button className="white gap-16" onClick={() => setWarehouseReplenishmentModal({ show: true })}>
                            <OrdersIcon />
                            {t('request_raw_materials')}
                        </button>

                        <div className="d-flex gap-8">
                            <button className="dark gap-16" onClick={() => setAcceptanceMaterialsModal(true)}>
                                <PlusIcon />
                                {t('create_order')}
                            </button>
                        </div>
                    </div>
                }
            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_orders')}
                            </div>
                            <div className="value">
                                {formatMoney(data?.counters?.total)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_work')}
                            </div>
                            <div className="value text-danger">
                                {formatMoney(data?.counters?.in_progress)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('planned')}
                            </div>
                            <div className="value text-warning">
                                {formatMoney(data?.counters?.planned)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('completed')}
                            </div>
                            <div className="value">
                                {formatMoney(data?.counters?.completed_today)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="d-flex gap-16 w-100">
                    <FilterSearch
                        value={filterData.search}
                        onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
                        placeholder={t('search_by_sku_or_name')}
                    />

                    <CustomSelect
                        options={statuses}
                        value={statuses.find(option => option.id === filterData?.status) || ''}
                        onChange={(option) => setFilterData({ ...filterData, status: option?.id })}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        placeholder={t('select_status')}
                    />

                    <button className="white third" onClick={() => exportFile()}>
                        <DownloadIcon />
                        {t('export')}
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('production_orders')}
                    </div>
                </div>

                <div className="table-responsive w-100">
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '10%' }}>{t('order_id')}</th>
                                <th style={{ width: '15%' }}>{t('name')}</th>
                                <th style={{ width: '5%' }}>{t('plan')}</th>
                                <th style={{ width: '5%' }}>{t('fact')}</th>
                                <th style={{ width: '5%' }}>{t('shipped')}</th>
                                <th style={{ width: '10%' }}>{t('progress')}</th>
                                <th style={{ minWidth: '110px' }} className="text-center">{t('start_date')}</th>
                                <th style={{ minWidth: '110px' }} className="text-center">{t('end_date')}</th>
                                <th style={{ width: '10%' }}>{t('status')}</th>
                                <th style={{ width: '15%' }}>{t('actions')}</th>
                            </tr>
                        </thead>

                        <tbody>
                            {data?.results?.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.order_number}</td>
                                    <td>{item.product_name}</td>
                                    <td>{formatMoney(item.planned_quantity)} {item.unit_short_name}</td>
                                    <td>{formatMoney(item.produced_quantity)} {item.unit_short_name}</td>
                                    <td>{formatMoney(item.transferred_to_fg_quantity)} {item.unit_short_name}</td>
                                    <td>
                                        <div className="d-flex align-items-center gap-8">
                                            <div className="w-100">
                                                <div className="progress-bar">
                                                    <div className="progress-percent" style={{ width: `${item.progress_percent}%` }}></div>
                                                </div>
                                            </div>
                                            <div>
                                                {formatMoney(item.progress_percent)}%
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center">{formatDate(item.planned_start_date)}</td>
                                    <td className="text-center">{formatDate(item.planned_end_date)}</td>
                                    <td>
                                        <div className={`alert alert-${item.status} w-fit-content`}>
                                            {t(item.status_label)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-8">
                                            {reduxAccount.role === "TECHNOLOGIST" &&
                                                <button className="transparent small" onClick={() => setRecipeModal({ show: true, id: item.id })}>
                                                    {t('details')}
                                                </button>
                                            }
                                            {item.status === 'planned' &&
                                                <button className="white small" onClick={() => inProgressOrder(item)}>
                                                    {t('start_work')}
                                                </button>
                                            }
                                            {(
                                                item.status === 'in_progress' ||
                                                (item.status === "completed" &&
                                                    item.available_for_fg_transfer > 0
                                                )) &&
                                                <button className="white small" onClick={() => setProduceModal({ show: true, item })}>
                                                    {t('production_output')}
                                                </button>
                                            }
                                            {item.status === 'in_progress' &&
                                                <button className="dark small" onClick={() => setUpdateProgressModal({ show: true, item })}>
                                                    {t('update_progress')}
                                                </button>
                                            }
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>


                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
                <UpdateProgress modal={updateProgressModal.show} setModal={() => setUpdateProgressModal({ show: false })}
                    item={updateProgressModal.item} getData={getData} />

                <RecipeModal modal={recipeModal.show} setModal={() => setRecipeModal({ show: false })} id={recipeModal.id} />

                <ProduceOrder modal={produceModal.show} setModal={() => setProduceModal({ show: false })}
                    item={produceModal.item} getData={getData} />

                {reduxAccount.role === "TECHNOLOGIST" &&
                    <>
                        <AcceptanceMaterials modal={acceptanceMaterialsModal} setModal={setAcceptanceMaterialsModal} getData={getData} />

                        <WarehouseReplenishment modal={warehouseReplenishmentModal.show} setModal={() => setWarehouseReplenishmentModal({ show: false })}
                            getData={getData} item={warehouseReplenishmentModal.item} />
                    </>
                }
            </div>
        </>
    )
}

export default Production